import { push } from 'connected-react-router';
import { LocationState, Path } from 'history';
import { isEqual } from 'lodash';
import * as React from 'react';
import { connect } from 'react-redux';
import { Link, RouteComponentProps, withRouter } from 'react-router-dom';
import { Dispatch } from 'redux';
import { ResourceProvider } from '../../resourceProvider';
import { ApplicationState, ConnectedReduxProps } from '../../store';
import { fetchRequestService } from '../../store/servicePage/actions';
import { TrackingProvider } from '../../tracking/trackingProvider';
import { CountryAvailability } from './../../components/countryAvailability';
import { Disclaimer } from './../../components/disclaimer';
import { GeneralAvailability } from './../../components/generalAvailability';
import { ServiceImage } from './../../components/serviceImage';
import { ServiceItem } from './../../components/serviceItem';
import { ServiceVideo } from './../../components/serviceVideo';


interface IPropsFromState {
    loading: boolean,
    data: SkodaAuto.Marvin.ServiceAvailability.Web.App.Models.Data.ServiceModel,
    errors?: string,
    dataFilter?: SkodaAuto.Marvin.ServiceAvailability.Web.App.Models.Filters.DataFilter,
    defaultCountry?: SkodaAuto.Marvin.ServiceAvailability.Web.App.Models.Data.CodeListItemModel,
    countries: SkodaAuto.Marvin.ServiceAvailability.Web.App.Models.Data.CodeListItemModel[],
    families: SkodaAuto.Marvin.ServiceAvailability.Web.App.Models.Data.ModelFamilyModel[],
    infotainments: SkodaAuto.Marvin.ServiceAvailability.Web.App.Models.Data.InfotaimentModel[];
    importerSettings: SkodaAuto.Marvin.ServiceAvailability.Web.App.Models.ImporterSettingsModel;
    trackingProvider: TrackingProvider;
    selectedCountry: SkodaAuto.Marvin.ServiceAvailability.Web.App.Models.Data.CodeListItemModel;
    selectedModel: SkodaAuto.Marvin.ServiceAvailability.Web.App.Models.Data.ModelFamilyModel;
    selectedInfotaiment: SkodaAuto.Marvin.ServiceAvailability.Web.App.Models.Data.InfotaimentModel;
    selectedProductionInterval: SkodaAuto.Marvin.ServiceAvailability.Web.App.Models.Data.ProductionIntervalModel;
}

interface IPropsFromDispatch {
    fetchRequest: typeof fetchRequestService,
    push: typeof push
}

type IServiceProps = IPropsFromState & IPropsFromDispatch & ConnectedReduxProps & RouteComponentProps<{ parentcode?: string; code: string }>

interface IServicePageState {
    trackPageView: boolean
}

class ServicePage extends React.Component<IServiceProps, IServicePageState> {
    constructor(props: IServiceProps) {
        super(props);

        this.goToDetail = this.goToDetail.bind(this);
        this.linkTo = this.linkTo.bind(this);
        this.onLinkToClick = this.onLinkToClick.bind(this);
        this.state = { trackPageView: false };
    }

    public componentDidMount() {
        this.setState({ trackPageView: true })
        this.fetchService();
    }

    public componentDidUpdate(prevProps: IServiceProps) {
        if (!isEqual(this.props.dataFilter, prevProps.dataFilter) && (isEqual(this.props.defaultCountry, prevProps.defaultCountry))) {
            this.props.push("/");
        }

        if (this.state.trackPageView && this.props.data) {
            const code = this.props.match.params.code;
            if (this.props.data.Code === code) {
                this.setState({ trackPageView: false })
                this.props.trackingProvider.trackPageView(this.props.dataFilter.ImporterBid, this.props.dataFilter.Culture, this.props.selectedCountry?.OriginValue, this.props.selectedInfotaiment?.Title, this.props.selectedModel?.Code, this.props.selectedProductionInterval?.From?.ProductionDate, this.props.data.OriginTitle);
            }
        }

        this.scrollTop();
    }

    private fetchService(): void {
        let code = this.props.match.params.code;
        let filter = this.props.dataFilter;
        this.props.fetchRequest(code, filter);
    }

    private canShowGeneralAvailability(): boolean {
        return !this.props.data.IsAvailable || (!this.props.dataFilter.FamilyCode && !this.props.dataFilter.InfotainmentCode && !this.props.dataFilter.ProductionFromCode);
    }

    private canShowServiceAvailability(): boolean {
        if (this.props.dataFilter.FamilyCode || this.props.dataFilter.InfotainmentCode || this.props.dataFilter.ProductionFromCode)
            return true;

        return false;
    }

    private canShowServiceAvailabilityPeriods(): boolean {
        return this.props.data.IsAvailable && !this.props.dataFilter.ProductionFromCode
    }

    private scrollTop(): void {
        window.scrollTo(0, 0);

        if (document.body && document.body.scrollTo) {
            document.body.scrollTo(0, 0);
        } else if (document.body.scrollTop) {
            document.body.scrollTop = 0;
        }
    }

    private renderCountries(): JSX.Element {
        let availableCountries = this.props.data.AvailableForCountries
            ? this.props.countries.filter((c) => this.props.data.AvailableForCountries.findIndex((i) => i.Code == c.Code) > -1)
            : [];

        return <CountryAvailability countries={availableCountries} collapsible={false} />
    }

    private goToDetail(code: string, originTitle: string): void {
        this.props.trackingProvider.trackButtonClick(originTitle, null, this.props.selectedCountry?.OriginValue, this.props.selectedInfotaiment?.Title, this.props.selectedModel?.Code, this.props.selectedProductionInterval?.From?.ProductionDate);
        this.props.push("/childservice/" + this.props.data.Code + "/" + code);
    }

    private linkTo() {
        let parentcode = this.props.match.params.parentcode;

        if (parentcode) {
            return "/service/" + parentcode;
        }
        else {
            return "/";
        }
    }

    private onLinkToClick() {
        if (!this.props.match.params.parentcode) {
            this.props.trackingProvider.trackPageView(this.props.dataFilter.ImporterBid, this.props.dataFilter.Culture, this.props.selectedCountry?.OriginValue, this.props.selectedInfotaiment?.Title, this.props.selectedModel?.Code, this.props.selectedProductionInterval?.From?.ProductionDate);
        }
    }

    private videoOnStart(video: SkodaAuto.Marvin.ServiceAvailability.Web.App.Models.Data.ServiceMediaModel): void {
        this.props.trackingProvider.trackVideoInteraction(video.Title ?? this.props.data.OriginTitle, 'Playback start', this.props.selectedCountry?.OriginValue, this.props.selectedInfotaiment?.Title, this.props.selectedModel?.Code, this.props.selectedProductionInterval?.From?.ProductionDate);
    }

    private videoOnPause(video: SkodaAuto.Marvin.ServiceAvailability.Web.App.Models.Data.ServiceMediaModel): void {
        this.props.trackingProvider.trackVideoInteraction(video.Title ?? this.props.data.OriginTitle, 'Playback pause', this.props.selectedCountry?.OriginValue, this.props.selectedInfotaiment?.Title, this.props.selectedModel?.Code, this.props.selectedProductionInterval?.From?.ProductionDate);
    }

    private videoOnProgess(video: SkodaAuto.Marvin.ServiceAvailability.Web.App.Models.Data.ServiceMediaModel, percentatage: number): void {
        this.props.trackingProvider.trackVideoReach(video.Title ?? this.props.data.OriginTitle, percentatage.toString(), this.props.selectedCountry?.OriginValue, this.props.selectedInfotaiment?.Title, this.props.selectedModel?.Code, this.props.selectedProductionInterval?.From?.ProductionDate);
    }

    public render(): JSX.Element {
        if (!this.props.data)
            return null;

        let service = this.props.data;
        let showServiceAvailability = this.canShowServiceAvailability();
        let showServiceAvailabilityPeriods = this.canShowServiceAvailabilityPeriods();
        let showGeneralAvailability = this.canShowGeneralAvailability();
        let linkTo = this.linkTo();

        return (
            <div className="container page service-page">
                <div className="container page-title">
                    <div className="back-navigation">
                        <Link to={linkTo} className="undecorated-link" onClick={this.onLinkToClick}>
                            <div></div>
                        </Link>
                    </div>
                    <table>
                        <tbody>
                            <tr>
                                <td className="service-title">
                                    <h1>{service.Title}</h1>
                                </td>
                                <td className="service-icon" >
                                    <img src={service.Icon} alt={service.Title} />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {
                    showServiceAvailability &&
                    <div className="container service-availability-container">
                        <div className="image-cell">
                            {service.IsAvailable ?
                                <div className='image-status-ok available'>
                                </div>
                                :
                                <span className='icon icon-status-warning-error not-available'></span>
                            }
                        </div>
                        <div className="title-cell">
                            <span className="span4">{service.IsAvailable ? ResourceProvider.getText('ServicePage.AvailableForSelectedCar') : ResourceProvider.getText('ServicePage.NotAvailableForSelectedCar')}</span>
                        </div>
                        {
                            showServiceAvailabilityPeriods && service.AvailableForProductions &&
                            <div className="productions-cell">
                                {
                                    service.AvailableForProductions && service.AvailableForProductions.map((period, index, array) =>
                                        <span className="span4" key={'serviceDetail_' + service.Code + '_period_' + index}>
                                            {
                                                period.To
                                                    ? ResourceProvider.getText('ServicePage.AvailableFrom') + ' ' + period.From.Title + ' ' + ResourceProvider.getText('ServicePage.AvailableTo') + ' ' + period.To.Title + (((index < array.length - 1) && array.length > 1) ? ', ' : '')
                                                    : ResourceProvider.getText('ServicePage.AvailableFrom') + ' ' + period.From.Title + (((index < array.length - 1) && array.length > 1) ? ', ' : '')
                                            }
                                        </span>
                                    )
                                }
                            </div>
                        }
                    </div>
                }

                <div className="container body-container">
                    {
                        service.Summary &&
                        <div className="row">
                            <div className="col-12 col-xl-24 col-lg-24 col-md-24 col-sm-24 summary-container position-col-reset">
                                <p>{service.Summary}</p>
                            </div>
                        </div>
                    }

                    {
                        service.Videos && service.Videos.length > 0 &&
                        <div className="row">
                            {
                                service.Videos.map((video) => <ServiceVideo key={video.Id.toString()} video={video} onStart={() => this.videoOnStart(video)} onPause={() => this.videoOnPause(video)} onProgress={(percentage) => this.videoOnProgess(video, percentage)} />)
                            }
                        </div>
                    }

                    {
                        service.Images && service.Images.length > 0 &&
                        <div className="row">
                            {
                                service.Images.map((image) => <ServiceImage key={image.Id.toString()} image={image} />)
                            }
                        </div>
                    }

                    {
                        service.Description &&
                        <div className="row">
                            <div className="description-container position-col-reset">
                                {
                                    <p>{service.Description}</p>
                                }
                            </div>
                        </div>
                    }

                    {
                        service.ChildServices && service.ChildServices.length > 0 &&
                        <div key={service.Id.toString()} className="service-group-container">
                            <h3>{service.Title + ' ' + ResourceProvider.getText('ServicePage.Subservices.Title')}</h3>
                            <div className="row">
                                {
                                    <div className="col-12 col-xl-24 col-lg-24 col-md-24 col-sm-24 position-col-reset">
                                        {service.ChildServices.map((childService, index) => <ServiceItem key={childService.Id.toString()} service={childService} orderInGroup={index + 1} showAvailability={false} appliedFilter={this.props.dataFilter} onClick={this.goToDetail} />)}
                                    </div>
                                }
                            </div>
                        </div>
                    }

                    {
                        showGeneralAvailability &&
                        <div className="row">
                            <div className="col-12 col-xl-24 col-lg-24 col-md-24 col-sm-24 position-col-reset">
                                <GeneralAvailability availability={service.GeneralAvailability} countries={this.props.countries} families={this.props.families} infotainments={this.props.infotainments} settings={this.props.importerSettings} />
                            </div>
                        </div>
                    }

                    {
                        !showGeneralAvailability &&
                        <div className="row">
                            <div className="col-12 col-xl-24 col-lg-24 col-md-24 col-sm-24 position-col-reset">
                                <div className="container general-availability-container">
                                    <div className="row">
                                        <h2>{ResourceProvider.getText('CountryAvailability.Title')}</h2>
                                    </div>
                                    {
                                        this.renderCountries()
                                    }
                                </div>
                            </div>
                        </div>
                    }

                    <div className="row">
                        <div className="col-12 col-xl-24 col-lg-24 col-md-24 col-sm-24 position-col-reset">
                            <Disclaimer />
                        </div>
                    </div>

                    {this.props.children}
                </div>
            </div>
        );
    }
}

const mapStateToProps = ({ servicePage, filter, common }: ApplicationState) => ({
    loading: servicePage.loading,
    errors: servicePage.errors,
    data: servicePage.data,
    dataFilter: filter.dataFilter,
    defaultCountry: filter.defaultCountry,
    countries: common.countries,
    families: common.families,
    infotainments: common.infotainments,
    importerSettings: common.importerSettings,
    trackingProvider: common.trackingProvider,
    selectedCountry: filter.selectedCountry,
    selectedModel: filter.selectedModel,
    selectedInfotaiment: filter.selectedInfotaiment,
    selectedProductionInterval: filter.selectedProductionInterval
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
    fetchRequest: (code: string, filter: SkodaAuto.Marvin.ServiceAvailability.Web.App.Models.Filters.DataFilter) => dispatch(fetchRequestService(code, filter)),
    push: (path: Path, state?: LocationState) => dispatch(push(path, state))
})

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withRouter(ServicePage))